/**
 * Open the job-shaped flow a starting point lands in, with its preset applied
 * (PRD-start-intents §5). The Start screen and the Navigator both call this,
 * so a starting point lands in the same place from either.
 */

import type { NavigateFunction } from 'react-router-dom';
import { useConfigurationsStore } from '@/stores/prioritization/configurationsStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import type { SessionPreset, StartEntry } from '@/lib/navigator/catalog';

/** What a person already said, pulled out by the Start guide. */
export interface StartExtraction {
  /** Short working title inferred from the person's own words. */
  suggestedTitle?: string;
  /** Options/candidates/topics they explicitly named — never invented. */
  options?: string[];
}

export async function launchStartPoint(
  entry: StartEntry,
  navigate: NavigateFunction,
  extraction?: StartExtraction | null,
): Promise<void> {
  const dest = entry.destination;
  switch (dest.kind) {
    case 'session': {
      const preset: SessionPreset = {
        ...dest.preset,
        ...(extraction?.suggestedTitle ? { suggestedName: extraction.suggestedTitle } : {}),
        ...(extraction?.options?.length ? { seedOptions: extraction.options } : {}),
      };
      navigate('/apps/session/new', { state: { startPreset: preset } });
      return;
    }
    case 'items':
      navigate('/apps/items', { state: { startCapture: true } });
      return;
    case 'viability':
      navigate('/apps/viability');
      return;
    case 'session-import':
      navigate('/apps/session-import');
      return;
    case 'cycle': {
      // Resolve the Doc 15 configuration id best-effort so the cycle
      // stays attributable; name-only if the lookup fails.
      const params = new URLSearchParams({ create: '1' });
      try {
        const workspaceId = useTenantStore.getState().currentWorkspaceId;
        if (workspaceId) {
          await useConfigurationsStore.getState().fetchConfigurations(workspaceId);
          const config = useConfigurationsStore
            .getState()
            .configurations.find((c) => c.slug === dest.configurationSlug);
          if (config) {
            params.set('config_id', config.id);
            params.set('config_name', config.name);
          }
        }
      } catch {
        // best-effort only — the cycle form works without a configuration
      }
      if (!params.has('config_name')) params.set('config_name', entry.title);
      navigate(`/prioritization/cycles?${params.toString()}`);
    }
  }
}
